import type { IconName } from '../../components/Icon';
import type { FieldType, FormField } from '../../types';

export const FIELD_ICON: Record<FieldType, IconName> = {
  text: 'text',
  textarea: 'textarea',
  number: 'number',
  phone: 'phone',
  email: 'email',
  date: 'date',
  boolean: 'toggle',
  select: 'list',
  multiselect: 'checklist',
  photo: 'camera',
  geo: 'pin',
  gstin: 'id',
  pan: 'id',
  route: 'map',
};

export const TYPE_LABEL: Record<FieldType, string> = {
  text: 'Text',
  textarea: 'Long text',
  number: 'Number',
  phone: 'Mobile number',
  email: 'Email',
  date: 'Date',
  boolean: 'Yes / No',
  select: 'Dropdown',
  multiselect: 'Multi-select',
  photo: 'Photo',
  geo: 'Location pin',
  gstin: 'GST number',
  pan: 'PAN',
  route: 'Area / Route',
};

export const HAS_OPTIONS = (type: FieldType) => type === 'select' || type === 'multiselect';

/**
 * Turn a label into a database key.
 *
 * The admin never sees this until they open the field, but it is what
 * every stored value is filed under and what an export column is named,
 * so it has to be stable and readable rather than a random id.
 */
export function keyFromLabel(label: string): string {
  const slug = label
    .toLowerCase()
    .replace(/['']/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40);

  // Keys must start with a letter.
  const safe = /^[a-z]/.test(slug) ? slug : `f_${slug}`;
  return safe.replace(/_+$/, '') || 'field';
}

export function uniqueKey(base: string, taken: Set<string>): string {
  if (!taken.has(base)) return base;
  for (let n = 2; n < 200; n += 1) {
    const candidate = `${base}_${n}`.slice(0, 40).replace(/_+$/, '');
    if (!taken.has(candidate)) return candidate;
  }
  return `${base}_${Date.now().toString(36)}`.slice(0, 40);
}

/** A brand new field, with sensible starting settings for its type. */
export function makeField(type: FieldType, taken: Set<string>): FormField {
  const label = TYPE_LABEL[type];
  const key = uniqueKey(keyFromLabel(label), taken);

  const field: FormField = {
    key,
    label,
    type,
    required: false,
    showInList: false,
    system: false,
  };

  if (HAS_OPTIONS(type)) {
    field.options = [{ value: 'option_1', label: 'Option 1' }];
  }
  if (type === 'gstin') field.label = 'GST No';
  if (type === 'pan') field.label = 'PAN No';
  if (type === 'phone') field.label = 'Contact No';
  if (type === 'email') field.label = 'Email Address';

  // Relabelling above changes the key too, while it is still unpublished.
  if (field.label !== label) {
    field.key = uniqueKey(keyFromLabel(field.label), taken);
  }

  return field;
}

/** Client-side mirror of the server's draft rules, for instant feedback. */
export function validateDraft(fields: FormField[]): Record<string, string> {
  const errors: Record<string, string> = {};
  const seen = new Set<string>();

  for (const field of fields) {
    if (!field.label.trim()) {
      errors[field.key] = 'Give this field a label';
    } else if (seen.has(field.key)) {
      errors[field.key] = `The key "${field.key}" is used twice`;
    } else if (HAS_OPTIONS(field.type) && !field.options?.length) {
      errors[field.key] = 'Add at least one option';
    } else if (HAS_OPTIONS(field.type)
      && new Set(field.options?.map((o) => o.value)).size !== field.options?.length) {
      errors[field.key] = 'Two options have the same value';
    } else if (field.min != null && field.max != null && field.min > field.max) {
      errors[field.key] = 'Maximum must be at least the minimum';
    }
    seen.add(field.key);
  }

  return errors;
}

/** True when two field lists are the same, so we do not save no-op drafts. */
export function sameFields(a: FormField[], b: FormField[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((field, i) => {
    const other = b[i];
    if (!other) return false;
    return field.key === other.key
      && field.label === other.label
      && field.type === other.type
      && Boolean(field.required) === Boolean(other.required)
      && Boolean(field.showInList) === Boolean(other.showInList)
      && (field.helpText ?? '') === (other.helpText ?? '')
      && (field.placeholder ?? '') === (other.placeholder ?? '')
      && field.min === other.min
      && field.max === other.max
      && field.maxLength === other.maxLength
      && JSON.stringify(field.options ?? null) === JSON.stringify(other.options ?? null);
  });
}
